import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CreditCard, 
  Lock, 
  Loader2, 
  AlertCircle, 
  CheckCircle,
  Shield,
  Calendar,
  User
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface StorefrontPaymentModalProps {
  open: boolean;
  onClose: () => void;
  token: string;
  amount: number;
  onSuccess: (transactionData: any) => void;
  shippingAddress: any;
  programServiceFee?: number;      // in cents
  clinicFacilitationFee?: number;  // in cents
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const StorefrontPaymentModal: React.FC<StorefrontPaymentModalProps> = ({
  open,
  onClose,
  token,
  amount,
  onSuccess,
  shippingAddress,
  programServiceFee,
  clinicFacilitationFee,
}) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'processing' | 'success'>('form');
  
  // Dummy form state
  const [cardNumber, setCardNumber] = useState('4111 1111 1111 1111');
  const [cardName, setCardName] = useState('John Doe');
  const [expiryDate, setExpiryDate] = useState('12/25');
  const [cvv, setCvv] = useState('123');

  // Format card number as user types
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setError(null);
    setStep('processing');

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success
      const dummyTransactionData = {
        transactionId: `txn_${Date.now()}`,
        amount: amount,
        status: 'completed',
        timestamp: new Date().toISOString(),
        last4: '1111',
        cardType: 'Visa'
      };

      setStep('success');
      
      setTimeout(() => {
        toast({
          title: 'Payment Successful',
          description: `Your payment of ${formatCurrency(amount)} has been processed successfully.`,
        });
        onSuccess(dummyTransactionData);
      }, 1500);
      
    } catch (err: any) {
      setStep('form');
      setError('Payment failed. Please try again.');
      toast({
        title: 'Payment Failed',
        description: err.message || 'An error occurred processing your payment.',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setStep('form');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Secure Payment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Progress indicator */}
          <div className="flex items-center justify-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${step === 'form' ? 'bg-blue-600' : 'bg-gray-300'}`} />
            <div className={`w-2 h-2 rounded-full ${step === 'processing' ? 'bg-blue-600' : 'bg-gray-300'}`} />
            <div className={`w-2 h-2 rounded-full ${step === 'success' ? 'bg-green-600' : 'bg-gray-300'}`} />
          </div>

          {step === 'form' && (
            <>
              {/* Amount Display */}
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="pt-4 space-y-2">
                  {programServiceFee !== undefined && clinicFacilitationFee !== undefined && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Program Service Fee</span>
                        <span className="font-medium">{formatCurrency(programServiceFee / 100)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Clinic Facilitation Fee</span>
                        <span className="font-medium">{formatCurrency(clinicFacilitationFee / 100)}</span>
                      </div>
                      <Separator />
                    </>
                  )}
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Total Amount</p>
                      <p className="text-xs text-gray-500 mt-1">Including tax</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(amount)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Card Form (Dummy) */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Card Number</Label>
                  <div className="relative">
                    <Input
                      placeholder="1234 5678 9012 3456"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      maxLength={19}
                      className="pl-10"
                    />
                    <CreditCard className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500">Test card: 4111 1111 1111 1111</p>
                </div>

                <div className="space-y-2">
                  <Label>Cardholder Name</Label>
                  <div className="relative">
                    <Input
                      placeholder="John Doe"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      className="pl-10"
                    />
                    <User className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <div className="relative">
                      <Input
                        placeholder="MM/YY"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        maxLength={5}
                        className="pl-10"
                      />
                      <Calendar className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>CVV</Label>
                    <div className="relative">
                      <Input
                        type="password"
                        placeholder="123"
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value)}
                        maxLength={4}
                        className="pl-10"
                      />
                      <Lock className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Billing Address Display */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs font-medium text-gray-700 mb-1">Billing Address</p>
                  <p className="text-sm text-gray-600">
                    {shippingAddress.line_1}
                    {shippingAddress.line_2 && <>, {shippingAddress.line_2}</>}
                  </p>
                  <p className="text-sm text-gray-600">
                    {shippingAddress.city}, {shippingAddress.stateInfo.code} {shippingAddress.postal_code}
                  </p>
                </div>
              </div>

              {/* Security Badges */}
              <div className="flex items-center justify-center gap-4 py-2">
                <Badge variant="outline" className="text-xs">
                  <Shield className="h-3 w-3 mr-1" />
                  SSL Encrypted
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Lock className="h-3 w-3 mr-1" />
                  PCI Compliant
                </Badge>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePayment}
                  disabled={isProcessing}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay {formatCurrency(amount)}
                </Button>
              </div>
            </>
          )}

          {step === 'processing' && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600" />
              <h3 className="text-lg font-semibold">Processing Payment...</h3>
              <p className="text-sm text-gray-600">
                Please wait while we securely process your payment.
              </p>
              <p className="text-xs text-gray-500">
                Do not close this window or refresh the page.
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="py-12 text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Payment Successful!</h3>
              <p className="text-sm text-gray-600">
                Your payment of {formatCurrency(amount)} has been processed successfully.
              </p>
              <Badge variant="outline" className="mx-auto">
                Transaction ID: txn_{Date.now()}
              </Badge>
              <p className="text-xs text-gray-500">
                Redirecting to confirmation page...
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StorefrontPaymentModal; 